/**
 * 感情的コピー辞書
 *
 * ガイドライン: 「YES/NO」は絶対禁止 → 感情的表現に変換
 * 全コンポーネントのボタンテキスト・通知メッセージはこの辞書から参照する。
 *
 * 【ガードレール】過剰なポエム化の禁止:
 * 「魂」など重すぎる言葉の多用を避け、「事実 + 温かい動詞」のバランスを厳守する。
 *
 * @see docs/GAMENICS_DESIGN_GUIDELINES.md ボタンテキスト変換表
 */

// ──── ボタンテキスト ────

export const COPY = {
  // 肯定アクション
  submit: '届ける！',
  confirm: 'これでいこう！',
  try: 'やってみる！',
  save: '大切にしまう',
  start: 'さっそくやろう！',
  record: '録音する',
  post: '作品を届ける！',

  // 否定・取消・やり直しアクション
  cancel: 'やっぱりやめる',
  skip: '今はやめておく',
  back: 'ちょっと戻る',
  close: '閉じる',
  delete: '手放す',
  logout: 'いったん離れる',
  retry: 'もう一度挑戦する',

  // ログイン・登録系
  login: 'ログイン',
  signup: '新規登録',
  themeList: '作品を探す',
  mypage: 'マイページ',
  admin: '管理者ツール',

  // 共創アクション（フェーズD）
  praise: '称賛する',
  praiseAI: 'AIの提案を見る',
  praiseManual: '自分の言葉で書く',
  addVoice: '声を上げる',
  addIllustration: '絵を上げる',
  addScript: '物語を上げる',
  collab: 'セッション提案',
  viewLineage: '系譜を見る',

  // 一般 UI
  more: '⋯ もっと',
  like: 'いいね',
  share: 'シェアする',
  embed: '埋め込みコード',
  gift: '応援する',
  searchThemes: '作品を検索する',
  proposeTheme: '新しい作品を生み出す',
  editProfile: 'プロフィールを整える',
  claimBonus: 'ボーナスを受け取る',
  join: '参加してみる！',
  backHome: '戻って出直す',
  vote: 'こっちに決めた！',
  praiseWithAI: 'AIに言葉を探してもらう',
  loadingThemes: '作品を集めています...',
  noItems: 'まだ誰もいないようです...',
  markAllRead: 'ぜんぶ読んだよ',
  
  // アップロード・作成関連 (Phase 5)
  uploading: '準備中...',
  uploadImage: 'お気に入りの一枚を選ぶ',
  saving: '大切にしまっています...',
  createTheme: '募集を始める',
  suggestTheme: '提案を届ける',
} as const;

// ──── 損失回避フレーミング ────

export const LOSS_FRAMING = {
  streakWarning: (days: number) =>
    `今日の表現を残さないと、連続記録（${days}日）が失われます`,

  profileIncomplete: (pct: number) =>
    `プロフィール完成度 ${pct}% — あと少しで表現の傾向分析が解放`,

  takeWarning: (count: number) =>
    `これまでに保存した${count}個のテイクが失われます`,

  contestUrgency: (hours: number) =>
    `この限定コンテストはあと${hours}時間で締め切り`,
} as const;

// ──── ツァイガルニク（未完了タスク）メッセージ ────

export const INCOMPLETE_TASKS = {
  voiceAnalysis: (remaining: number) =>
    `表現の傾向分析まで、あと${remaining}回の作品が必要です`,

  nextBadge: (remaining: number) =>
    `あと${remaining}作品でバッジ獲得`,

  lineageMissing: (type: 'script' | 'illustration' | 'voice') => {
    const labels = { script: '物語', illustration: '絵', voice: '声' };
    return `この系譜にはまだ${labels[type]}がありません — 最初の一人になろう`;
  },
} as const;

// ──── ロール表示ラベル ────

export const ROLE_LABELS = {
  script: '物語',
  illustration: '絵',
  voice: '声',
} as const;

export type RoleType = keyof typeof ROLE_LABELS;
